// Quick-edit modal for the three intake identifiers (driver's license #,
// SSN, ZIP) on an existing job. Wired to the BackgroundChecksStatus
// "Add now" buttons on skipped MVC rows — when a paralegal sees that
// MVC scrapes were skipped because the DL number wasn't on file, they
// can add it here without leaving the page.
//
// Save flow:
//   1. PATCH /api/jobs/:id/identifiers with the new values
//   2. Server updates the row + automatically re-runs external fanout,
//      so any source that was previously skipped now gets queued.
//   3. UI closes the modal + lets the parent refresh state via onSaved.
//
// The same auto-formatters as the lookup form apply (the user gets
// consistent shape feedback regardless of which surface they're using).

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { runWithToast } from "../lib/downloadToast";
import {
  formatSsn,
  formatZip,
  formatDl,
  isValidSsn,
  isValidZip,
  isValidDl,
} from "../lib/identifierFormatters";

export type IdentifierField = "dl" | "ssn" | "zip";

interface Props {
  jobId: string;
  /** Current values (may be null if unset). The form starts with these. */
  initial: {
    licenseNumber: string | null;
    ssn: string | null;
    zipCode: string | null;
  };
  /** Which field to put initial focus on. The user clicked the "Add"
   *  button next to a specific missing field; that's the one they want
   *  to fill in first. */
  focusField: IdentifierField;
  onClose: () => void;
  /** Called after a successful save with the (possibly newly-eligible)
   *  list of sources the server enqueued. Parent can use this to refresh
   *  the BackgroundChecksStatus / external lookup state. */
  onSaved: (result: { enqueued: string[] }) => void;
}

interface PatchResponse {
  ok: boolean;
  enqueued: string[];
  skipped: Array<{ source: string; reason: string }>;
}

export function IdentifiersModal({ jobId, initial, focusField, onClose, onSaved }: Props): JSX.Element {
  const [dl, setDl] = useState(initial.licenseNumber ?? "");
  const [ssn, setSsn] = useState(initial.ssn ?? "");
  const [zip, setZip] = useState(initial.zipCode ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dlRef = useRef<HTMLInputElement>(null);
  const ssnRef = useRef<HTMLInputElement>(null);
  const zipRef = useRef<HTMLInputElement>(null);

  // Focus the requested field on mount + lock body scroll while open.
  useEffect(() => {
    const targets: Record<IdentifierField, React.RefObject<HTMLInputElement>> = {
      dl: dlRef,
      ssn: ssnRef,
      zip: zipRef,
    };
    targets[focusField].current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [focusField, onClose]);

  const dlValid = dl.length === 0 || isValidDl(dl);
  const ssnValid = ssn.length === 0 || isValidSsn(ssn);
  const zipValid = zip.length === 0 || isValidZip(zip);
  const allShapesOk = dlValid && ssnValid && zipValid;
  const hasAnyValue = dl.length > 0 || ssn.length > 0 || zip.length > 0;

  const save = async (): Promise<void> => {
    if (!allShapesOk) return;
    if (!hasAnyValue) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Send only fields the user actually filled in. Empty strings are
      // treated as "no change" by the server (the column keeps its
      // current value). To clear a field, the user would explicitly
      // delete its content — we don't currently expose that flow.
      const body: Record<string, string> = {};
      if (dl.length > 0) body.licenseNumber = dl;
      if (ssn.length > 0) body.ssn = ssn;
      if (zip.length > 0) body.zipCode = zip;
      const dataRef: { current: PatchResponse | null } = { current: null };
      await runWithToast({
        label: "Saving identifiers",
        successDetail: "Saved",
        run: async () => {
          const res = await fetch(`/api/jobs/${jobId}/identifiers`, {
            method: "PATCH",
            headers: { "content-type": "application/json" },
            credentials: "include",
            body: JSON.stringify(body),
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          dataRef.current = (await res.json()) as PatchResponse;
        },
      });
      onSaved({ enqueued: dataRef.current?.enqueued ?? [] });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const modal = (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add intake identifiers"
        className="modal-card modal-card-narrow"
      >
        <header className="modal-hero">
          <div className="modal-hero-eyebrow">Identifiers</div>
          <h2 className="modal-hero-title">Add Intake Identifiers</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="modal-hero-close"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </header>
        <div className="modal-body">
        <p
          className="mb-5"
          style={{ fontSize: 13, color: "var(--color-text-muted)", lineHeight: 1.5 }}
        >
          Filling these in unlocks the MVC scrapes (surcharges + restoration fees).
          Saving will automatically queue any newly-eligible source, no need to also click Retry.
        </p>

        <div className="grid gap-4">
          <div className="flex flex-col gap-1.5">
            <label
              className="text-[10px] font-semibold uppercase tracking-[0.18em]"
              style={{ color: "var(--color-text-dim)" }}
            >
              Driver's license / ID #
            </label>
            <input
              ref={dlRef}
              className="input mono"
              placeholder="NJ DL or state ID"
              autoComplete="off"
              maxLength={20}
              value={dl}
              onChange={(e) => setDl(formatDl(e.target.value))}
              style={{
                borderColor: dlValid ? undefined : "var(--color-rose-hi)",
              }}
            />
            {dl.length > 0 && !dlValid && (
              <div className="text-[11px]" style={{ color: "var(--color-rose-hi)" }}>
                Expected 8–20 letters/digits starting with a letter.
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              className="text-[10px] font-semibold uppercase tracking-[0.18em]"
              style={{ color: "var(--color-text-dim)" }}
            >
              SSN
            </label>
            <input
              ref={ssnRef}
              className="input mono"
              placeholder="XXX-XX-XXXX"
              autoComplete="off"
              inputMode="numeric"
              maxLength={11}
              value={ssn}
              onChange={(e) => setSsn(formatSsn(e.target.value))}
              style={{
                borderColor: ssnValid ? undefined : "var(--color-rose-hi)",
              }}
            />
            {ssn.length > 0 && !ssnValid && (
              <div className="text-[11px]" style={{ color: "var(--color-rose-hi)" }}>
                Expected 9 digits.
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              className="text-[10px] font-semibold uppercase tracking-[0.18em]"
              style={{ color: "var(--color-text-dim)" }}
            >
              ZIP code
            </label>
            <input
              ref={zipRef}
              className="input mono"
              placeholder="5 digits"
              autoComplete="postal-code"
              inputMode="numeric"
              maxLength={5}
              value={zip}
              onChange={(e) => setZip(formatZip(e.target.value))}
              style={{
                borderColor: zipValid ? undefined : "var(--color-rose-hi)",
              }}
            />
            {zip.length > 0 && !zipValid && (
              <div className="text-[11px]" style={{ color: "var(--color-rose-hi)" }}>
                Expected exactly 5 digits.
              </div>
            )}
          </div>
        </div>

        {error && (
          <div
            className="mt-4 rounded px-3 py-2 text-[13px]"
            style={{
              background: "rgba(255,120,120,0.10)",
              color: "var(--color-rose-hi)",
              border: "1px solid rgba(255,120,120,0.30)",
            }}
          >
            {error}
          </div>
        )}

        </div>
        <div className="modal-footer">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="btn btn-ghost"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !allShapesOk}
            className="btn btn-primary"
          >
            {saving ? "Saving…" : "Save & re-queue"}
          </button>
        </div>
      </div>
    </>
  );

  return createPortal(modal, document.body);
}
